/**
 * Photos : la galerie officielle de l'organisation (éditeur du site) et les photos publiées
 * par les visiteurs sur l'organisation et ses lieux.
 */
import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { PrimaryGalleryEditor } from '@web/features/organizations/components/PrimaryGalleryEditor'
import { useOrganization } from '@web/features/organizations/hooks/useOrganization'
import { fetchInsights, photoThumbUrl } from '../api'
import { Thumb } from '../components/Thumb'
import { useOrg } from '../utils/orgContext'
import { formatDate } from '../utils/format'
import { publicLocationUrl } from '../utils/links'

export function PhotosPage() {
  const { t } = useTranslation('gestion')
  const { orgId } = useOrg()
  const [tab, setTab] = useState<'official' | 'community'>('official')
  const { data: org } = useOrganization(orgId)
  const { data, isLoading } = useQuery({
    queryKey: ['insights', orgId, 365, 'photos'],
    queryFn: ({ signal }) => fetchInsights(orgId, 365, signal, 200),
    enabled: tab === 'community',
  })

  return (
    <div className="g-page">
      <div className="g-page__head">
        <h1 className="g-page__title">{t('photos.titre')}</h1>
      </div>
      <div className="g-tabs" role="tablist">
        <button type="button" role="tab" aria-selected={tab === 'official'} className={`g-tab${tab === 'official' ? ' g-tab--active' : ''}`} onClick={() => setTab('official')}>
          {t('photos.officielles')}
        </button>
        <button type="button" role="tab" aria-selected={tab === 'community'} className={`g-tab${tab === 'community' ? ' g-tab--active' : ''}`} onClick={() => setTab('community')}>
          {t('photos.visiteurs')}
        </button>
      </div>

      {tab === 'official' && (
        <section className="g-card g-embed">
          <p className="g-muted">{t('photos.intro_officielles')}</p>
          {org && <PrimaryGalleryEditor organizationId={orgId} primaryGallery={org.primary_gallery} />}
        </section>
      )}

      {tab === 'community' && (
        <section>
          <p className="g-muted">{t('photos.intro_visiteurs')}</p>
          {isLoading && <p className="g-muted">{t('commun.chargement')}</p>}
          {data && data.recent_photos.length === 0 && (
            <div className="g-empty">
              <span className="mdi mdi-image-off-outline g-empty__icon" aria-hidden />
              <p>{t('photos.aucune')}</p>
            </div>
          )}
          <div className="g-gallery">
            {data?.recent_photos.map((p) => (
              <figure key={p.id} className="g-gallery__item">
                <a href={publicLocationUrl({ id: p.location_id, type: p.location_type })} target="_blank" rel="noreferrer">
                  <Thumb src={photoThumbUrl(p.id, 480)} alt={p.location_name} />
                </a>
                <figcaption>
                  <strong>{p.location_name}</strong>
                  <span className="g-muted">
                    {p.author} · {formatDate(p.created_at)}
                  </span>
                </figcaption>
              </figure>
            ))}
          </div>
        </section>
      )}
    </div>
  )
}
